import { useState, useMemo, useEffect } from "react"
import { useOutletContext } from "react-router-dom"
import { FileSearch, Plus, Trash2, Edit2, CheckCircle2, AlertTriangle, Clock, RefreshCw, X } from "lucide-react"
import { differenceInDays, addMonths, subDays, format, parseISO, isValid } from "date-fns"
import ToolShell from "../components/ToolShell"
import ToolFAQ from "../components/ToolFAQ"
import CTABox from "../components/CTABox"
import RelatedToolsList from "../components/RelatedToolsList"
import { ToolDef } from "../toolRegistry"

type LeaseStatus = "active" | "expiring-soon" | "expired" | "rolling"
type LeaseTerm = "month-to-month" | "6" | "12" | "18" | "24" | "custom"
type NoticePeriod = "30" | "60" | "90" | "custom"

interface LeaseEntry {
  id: string
  propertyName: string
  tenantName: string
  startDate: string
  term: LeaseTerm
  customEndDate: string
  noticePeriod: NoticePeriod
  customNoticeDays: string
}

const TERM_MONTHS: Record<LeaseTerm, number> = {
  "month-to-month": 0,
  "6": 6,
  "12": 12,
  "18": 18,
  "24": 24,
  "custom": 0,
}

const NOTICE_DAYS: Record<NoticePeriod, number> = {
  "30": 30,
  "60": 60,
  "90": 90,
  "custom": 30,
}

function getLeaseEnd(entry: LeaseEntry): Date | null {
  if (entry.term === "month-to-month") return null
  if (entry.term === "custom") {
    if (!entry.customEndDate) return null
    const d = parseISO(entry.customEndDate)
    return isValid(d) ? d : null
  }
  if (!entry.startDate) return null
  const start = parseISO(entry.startDate)
  if (!isValid(start)) return null
  return addMonths(start, TERM_MONTHS[entry.term])
}

function getNoticeDays(entry: LeaseEntry): number {
  if (entry.noticePeriod === "custom") return parseInt(entry.customNoticeDays) || 30
  return NOTICE_DAYS[entry.noticePeriod]
}

function getStatus(entry: LeaseEntry): LeaseStatus {
  if (entry.term === "month-to-month") return "rolling"
  const endDate = getLeaseEnd(entry)
  if (!endDate) return "active"
  const today = new Date()
  const days = differenceInDays(endDate, today)
  if (days < 0) return "expired"
  if (days < 60) return "expiring-soon"
  return "active"
}

const STATUS_CONFIG: Record<LeaseStatus, { label: string; icon: typeof CheckCircle2; cls: string; dot: string }> = {
  active: { label: "Active", icon: CheckCircle2, cls: "bg-green-50 text-green-700 border-green-200", dot: "bg-green-500" },
  "expiring-soon": { label: "Expiring Soon", icon: AlertTriangle, cls: "bg-amber-50 text-amber-700 border-amber-200", dot: "bg-amber-500" },
  expired: { label: "Expired", icon: X, cls: "bg-red-50 text-red-700 border-red-200", dot: "bg-red-500" },
  rolling: { label: "Rolling / M-t-M", icon: RefreshCw, cls: "bg-blue-50 text-blue-700 border-blue-200", dot: "bg-blue-500" },
}

const EMPTY_FORM: Omit<LeaseEntry, "id"> = {
  propertyName: "",
  tenantName: "",
  startDate: "",
  term: "12",
  customEndDate: "",
  noticePeriod: "60",
  customNoticeDays: "",
}

const SOFT_LIMIT = 5

export default function LeaseExpiryTrackerPage() {
  const { tool } = useOutletContext<{ tool: ToolDef; currency: string }>()

  const [entries, setEntries] = useState<LeaseEntry[]>(() => {
    try {
      const saved = localStorage.getItem("rkp_lease_expiry_tracker")
      return saved ? JSON.parse(saved) : []
    } catch {
      return []
    }
  })

  const [showForm, setShowForm] = useState(false)
  const [editingId, setEditingId] = useState<string | null>(null)
  const [form, setForm] = useState<Omit<LeaseEntry, "id">>(EMPTY_FORM)

  useEffect(() => {
    localStorage.setItem("rkp_lease_expiry_tracker", JSON.stringify(entries))
  }, [entries])

  const today = useMemo(() => new Date(), [])

  const statusCounts = useMemo(() => {
    const counts: Record<LeaseStatus, number> = { active: 0, "expiring-soon": 0, expired: 0, rolling: 0 }
    entries.forEach(e => counts[getStatus(e)]++)
    return counts
  }, [entries])

  function handleSave() {
    if (!form.propertyName.trim()) return
    if (editingId) {
      setEntries(prev => prev.map(e => e.id === editingId ? { ...form, id: editingId } : e))
    } else {
      setEntries(prev => [...prev, { ...form, id: crypto.randomUUID() }])
    }
    setForm(EMPTY_FORM)
    setShowForm(false)
    setEditingId(null)
  }

  function handleEdit(entry: LeaseEntry) {
    const { id, ...rest } = entry
    setForm(rest)
    setEditingId(id)
    setShowForm(true)
    window.scrollTo({ top: 0, behavior: "smooth" })
  }

  function handleDelete(id: string) {
    setEntries(prev => prev.filter(e => e.id !== id))
  }

  function handleCancel() {
    setForm(EMPTY_FORM)
    setEditingId(null)
    setShowForm(false)
  }

  const sortedEntries = useMemo(() => {
    const order: LeaseStatus[] = ["expired", "expiring-soon", "active", "rolling"]
    return [...entries].sort((a, b) => order.indexOf(getStatus(a)) - order.indexOf(getStatus(b)))
  }, [entries])

  const atSoftLimit = entries.length >= SOFT_LIMIT

  return (
    <div className="space-y-8">
      <ToolShell>
        <div className="p-6 space-y-6">

          {/* Summary Bar */}
          {entries.length > 0 && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              {(Object.entries(statusCounts) as [LeaseStatus, number][]).map(([status, count]) => {
                const cfg = STATUS_CONFIG[status]
                const Icon = cfg.icon
                return (
                  <div key={status} className={`rounded-xl border p-3 flex items-center gap-2 ${cfg.cls}`}>
                    <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                    <div>
                      <p className="text-xs font-semibold">{count}</p>
                      <p className="text-xs opacity-75">{cfg.label}</p>
                    </div>
                    <Icon className="w-4 h-4 ml-auto opacity-50" />
                  </div>
                )
              })}
            </div>
          )}

          {/* Add / Edit Form */}
          {showForm && (
            <div className="rounded-2xl border border-primary/20 bg-primary/5 p-5 space-y-4">
              <div className="flex items-center justify-between">
                <p className="text-xs font-semibold text-slate-600 uppercase tracking-wide">
                  {editingId ? "Edit Property" : "Add Property"}
                </p>
                <button onClick={handleCancel} className="text-slate-400 hover:text-slate-600 transition-colors">
                  <X className="w-4 h-4" />
                </button>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Property Name / Address *</label>
                  <input
                    type="text"
                    value={form.propertyName}
                    onChange={e => setForm(f => ({ ...f, propertyName: e.target.value }))}
                    placeholder="e.g. Flat 3A, Baker Street"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Tenant Name (optional)</label>
                  <input
                    type="text"
                    value={form.tenantName}
                    onChange={e => setForm(f => ({ ...f, tenantName: e.target.value }))}
                    placeholder="e.g. Emma Johnson"
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Lease Start Date</label>
                  <input
                    type="date"
                    value={form.startDate}
                    onChange={e => setForm(f => ({ ...f, startDate: e.target.value }))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Lease Term</label>
                  <select
                    value={form.term}
                    onChange={e => setForm(f => ({ ...f, term: e.target.value as LeaseTerm }))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="month-to-month">Month-to-Month</option>
                    <option value="6">6 Months</option>
                    <option value="12">12 Months</option>
                    <option value="18">18 Months</option>
                    <option value="24">24 Months</option>
                    <option value="custom">Custom End Date</option>
                  </select>
                </div>

                {form.term === "custom" && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Custom End Date</label>
                    <input
                      type="date"
                      value={form.customEndDate}
                      onChange={e => setForm(f => ({ ...f, customEndDate: e.target.value }))}
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-xs font-medium text-slate-500 mb-1">Notice Period Required</label>
                  <select
                    value={form.noticePeriod}
                    onChange={e => setForm(f => ({ ...f, noticePeriod: e.target.value as NoticePeriod }))}
                    className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                  >
                    <option value="30">30 Days</option>
                    <option value="60">60 Days</option>
                    <option value="90">90 Days</option>
                    <option value="custom">Custom</option>
                  </select>
                </div>

                {form.noticePeriod === "custom" && (
                  <div>
                    <label className="block text-xs font-medium text-slate-500 mb-1">Custom Notice Days</label>
                    <input
                      type="number"
                      min="1"
                      value={form.customNoticeDays}
                      onChange={e => setForm(f => ({ ...f, customNoticeDays: e.target.value }))}
                      placeholder="e.g. 45"
                      className="w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                    />
                  </div>
                )}
              </div>

              <div className="flex gap-3 pt-1">
                <button
                  onClick={handleSave}
                  disabled={!form.propertyName.trim()}
                  className="px-5 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {editingId ? "Update Property" : "Save Property"}
                </button>
                <button onClick={handleCancel} className="px-4 py-2 text-sm text-slate-600 hover:text-slate-900 transition-colors">
                  Cancel
                </button>
              </div>
            </div>
          )}

          {/* Add Button + soft limit CTA */}
          {!showForm && (
            <div className="flex items-center gap-4 flex-wrap">
              <button
                onClick={() => setShowForm(true)}
                className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-semibold rounded-lg hover:bg-primary/90 transition-colors"
              >
                <Plus className="w-4 h-4" />
                Add Property
              </button>

              {atSoftLimit && (
                <p className="text-sm text-slate-600">
                  Tracking {entries.length} properties.{" "}
                  <a href="/register" className="text-primary font-semibold hover:underline">
                    Sign up to track unlimited properties in RentKeep →
                  </a>
                </p>
              )}
            </div>
          )}

          {/* Empty State */}
          {entries.length === 0 && !showForm && (
            <div className="text-center py-16 text-slate-400">
              <Clock className="w-12 h-12 mx-auto mb-3 opacity-30" />
              <p className="font-medium text-slate-500">No properties tracked yet</p>
              <p className="text-sm mt-1">Add your first property to start tracking lease expiry dates</p>
            </div>
          )}

          {/* Cards Grid */}
          {sortedEntries.length > 0 && (
            <div className="grid sm:grid-cols-2 xl:grid-cols-3 gap-4">
              {sortedEntries.map(entry => {
                const status = getStatus(entry)
                const cfg = STATUS_CONFIG[status]
                const Icon = cfg.icon
                const endDate = getLeaseEnd(entry)
                const noticeDays = getNoticeDays(entry)
                const daysUntilExpiry = endDate ? differenceInDays(endDate, today) : null
                const actByDate = endDate ? subDays(endDate, noticeDays) : null
                const daysUntilActBy = actByDate ? differenceInDays(actByDate, today) : null

                return (
                  <div key={entry.id} className={`rounded-2xl border ${cfg.cls} overflow-hidden`}>
                    {/* Card Header */}
                    <div className="flex items-start justify-between p-4 pb-3">
                      <div className="flex items-center gap-2 flex-1 min-w-0">
                        <div className={`w-2 h-2 rounded-full flex-shrink-0 ${cfg.dot}`} />
                        <span className="text-xs font-semibold uppercase tracking-wide truncate">{cfg.label}</span>
                      </div>
                      <div className="flex items-center gap-1 flex-shrink-0">
                        <button
                          onClick={() => handleEdit(entry)}
                          className="p-1.5 rounded-lg hover:bg-black/10 transition-colors text-current opacity-60 hover:opacity-100"
                          title="Edit"
                        >
                          <Edit2 className="w-3.5 h-3.5" />
                        </button>
                        <button
                          onClick={() => handleDelete(entry.id)}
                          className="p-1.5 rounded-lg hover:bg-black/10 transition-colors text-current opacity-60 hover:opacity-100"
                          title="Remove"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>

                    {/* Property & Tenant */}
                    <div className="px-4 pb-3">
                      <p className="font-bold text-slate-900 text-sm leading-snug">{entry.propertyName}</p>
                      {entry.tenantName && (
                        <p className="text-xs text-slate-500 mt-0.5">Tenant: {entry.tenantName}</p>
                      )}
                    </div>

                    {/* Date Info */}
                    <div className="mx-4 mb-3 bg-white/60 rounded-xl p-3 space-y-2">
                      {status === "rolling" ? (
                        <div className="flex items-center gap-2 text-sm">
                          <RefreshCw className="w-4 h-4 text-blue-500 flex-shrink-0" />
                          <span className="text-slate-700 font-medium">Month-to-month lease — no fixed end date</span>
                        </div>
                      ) : endDate ? (
                        <>
                          <div className="grid grid-cols-2 gap-2 text-xs">
                            <div>
                              <p className="text-slate-500 font-medium">Lease Ends</p>
                              <p className="font-bold text-slate-900 mt-0.5">{format(endDate, "MMM d, yyyy")}</p>
                            </div>
                            {actByDate && (
                              <div>
                                <p className="text-slate-500 font-medium">Act By (notice)</p>
                                <p className="font-bold text-slate-900 mt-0.5">{format(actByDate, "MMM d, yyyy")}</p>
                              </div>
                            )}
                          </div>

                          <div className="text-xs text-slate-600 border-t border-black/5 pt-2">
                            {daysUntilExpiry !== null && daysUntilExpiry < 0 ? (
                              <span className="text-red-600 font-semibold">Expired {Math.abs(daysUntilExpiry)} days ago</span>
                            ) : daysUntilExpiry !== null ? (
                              <span>
                                <span className="font-semibold">{daysUntilExpiry} days</span> remaining
                                {daysUntilActBy !== null && daysUntilActBy > 0 && (
                                  <span className="text-amber-700 ml-1">· Act within {daysUntilActBy} days</span>
                                )}
                                {daysUntilActBy !== null && daysUntilActBy <= 0 && (
                                  <span className="text-red-600 font-semibold ml-1">· Notice overdue!</span>
                                )}
                              </span>
                            ) : null}
                          </div>
                        </>
                      ) : (
                        <p className="text-xs text-slate-500">No end date set</p>
                      )}
                    </div>

                    {/* Footer */}
                    {entry.startDate && (
                      <div className="px-4 pb-3">
                        <p className="text-xs text-slate-400">
                          Started {isValid(parseISO(entry.startDate)) ? format(parseISO(entry.startDate), "MMM d, yyyy") : "—"} · {noticeDays}-day notice required
                        </p>
                      </div>
                    )}
                  </div>
                )
              })}
            </div>
          )}

          {/* Soft limit teaser */}
          {entries.length >= 3 && (
            <div className="bg-slate-800 text-white rounded-2xl p-5 text-center">
              <p className="font-semibold text-sm">You're tracking {entries.length} {entries.length === 1 ? "property" : "properties"}.</p>
              <p className="text-slate-300 text-xs mt-1 mb-3">
                Sign up to RentKeep to manage leases, rent records, and get automated renewal reminders — all in one place.
              </p>
              <a
                href="/register"
                className="inline-flex items-center px-5 py-2 bg-white text-slate-900 text-sm font-semibold rounded-xl hover:bg-slate-50 transition-colors"
              >
                Get automated alerts — free
              </a>
            </div>
          )}

        </div>
      </ToolShell>

      <ToolFAQ
        items={[
          {
            q: "When should I send a non-renewal notice to my tenant?",
            a: "Most residential leases require 30–90 days written notice before lease end. Check your lease agreement and local tenancy laws. The 'Act By' date in this tracker is calculated by subtracting your required notice period from the lease end date.",
          },
          {
            q: "What happens if a lease expires and neither party acts?",
            a: "In most jurisdictions, a fixed-term lease automatically converts to a periodic (month-to-month) tenancy when it expires without a renewal agreement or notice. Both parties typically retain the same rights and obligations — the main difference is that either party can now end the tenancy with proper notice.",
          },
          {
            q: "Is my tracker data saved if I close the browser?",
            a: "Yes. This tracker saves data locally in your browser's localStorage. Your entries will persist across page refreshes and browser restarts on the same device. However, they are not synced across devices — for that, sign up for a free account.",
          },
        ]}
      />

      <CTABox
        icon={FileSearch}
        heading="Get automated lease expiry alerts"
        body={tool.ctaText}
        ctaLabel="Start free"
        ctaHref="/register"
      />

      <RelatedToolsList relatedSlugs={tool.related} />
    </div>
  )
}
